import { Component, Input, OnInit } from '@angular/core'
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router'
import { Observable, filter, map, startWith, tap } from 'rxjs'
import { Breadcrumb, BreadcrumbLink } from './breadcrumb'
import { BreadcrumbService } from './breadcrumb.service'

/**
 *
 * Renders breadcrumbs for the activated route.
 *
 * Breadcrumbs are derived from the route hierarchy. BreadcrumbService traverses the route hierarchy top-down and looks
 * for a breadcrumb string in the route data, using that and the route's URL to build a Breadcrumb item.
 *
 * For dynamic breadcrumb, a data resolver can be used by implementing the helper type BreadcrumbResolver and returning
 * the desired breadcrumb label (e.g.: fetching a court system's name by its id).
 *
 * To hide breadcrumbs on a page, add a `hideBreadcrumbs: true` entry to the route data.
 *
 * @example
 *
 * // Route setup
 *
 * [{
 *   path: '',
 *   data: {
 *     breadcrumb: 'Breadcrumb for parent route'
 *   },
 *   children: [
 *     {
 *       path: 'child',
 *       resolve: {
 *         data: CustomBreadcrumbResolver
 *       }
 *     }
 *   ]
 * }]
 *
 * // CustomBreadcrumbResolver
 *
 * export class CustomBreadcrumbResolver implements BreadcrumbResolver {
 *   resolve (
 *     route: ActivatedRouteSnapshot,
 *     state: RouterStateSnapshot
 *   ): (string | undefined) | Promise<string | undefined> | Observable<string | undefined> {
 *     return 'Dynamic breadcrumb label'
 *   }
 * }
 *
 * // Resulting breadcrumb for the 'child' route
 *
 * [
 *   {
 *     label: 'Breadcrumb for parent route',
 *     url: '/'
 *   },
 *   {
 *     label: 'Dynamic breadcrumb label',
 *     url: '/child
 *   }
 * ]
 *
 */
@Component({
  selector: 'ftr-breadcrumbs',
  templateUrl: './breadcrumbs.component.html',
  styleUrls: ['./breadcrumbs.component.css'],
})
export class BreadcrumbsComponent implements OnInit {
  @Input() showBackToSearchResults: boolean
  breadcrumbs$: Observable<Breadcrumb[]>

  /**
   * When true adds the collapsible class to hide the full breadcrumb in smaller screens.
   * The default is false as the full breadcrumb should only be hidden in the playback page.
   */
  @Input() collapseOnSmallDevices: boolean = false

  /*
   * Allows a container to force the breadcrumbs to never display the
   * expanded crumbs. This is useful, for example, if we want to ensure the
   * crumbs don't collide with the content of the new Playback Context Bar
   */
  @Input() forceBreadcrumbsCollapse: boolean = false

  /**
   * There are situations where we want to hide the back text in the breadcrumb, such as on small devices when we are
   * streaming live - since on real time orders there are lots of items in the context bar and we need the space.
   */
  @Input() forceHideBreadcrumbBackText: boolean = false

  /**
   * Work-around to cater for different page alignments.
   * Allows a nested component to display and align the breadcrumb component
   * independent of the ftr-page component.
   */
  @Input() overrideHideBreadcrumbs = false

  /**
   * Allows a container to override the default 'Back' text that is displayed when breadcrumbs are collapsed.
   */
  @Input() alternativeBreadcrumbBackText: string | undefined

  previousPageLink: BreadcrumbLink | undefined

  constructor(
    private readonly breadcrumbsService: BreadcrumbService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.breadcrumbs$ = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.breadcrumbsService.getBreadcrumbs(this.activatedRoute.root, this.overrideHideBreadcrumbs)),
      startWith(this.breadcrumbsService.getBreadcrumbs(this.activatedRoute.root, this.overrideHideBreadcrumbs)),
      map(breadcrumbs => (breadcrumbs.length === 1 && breadcrumbs[0].link.url === '/' ? [] : breadcrumbs)),
      tap(breadcrumbs => (this.previousPageLink = breadcrumbs.length > 1 ? breadcrumbs.at(-2)?.link : undefined)),
    )
  }

  getPreviousPageQueryParams(queryParams: Params): Params {
    return { ...queryParams, back: true }
  }
}
